// 계약 관리 — 설정(C2c2): «부서»(편집 등급 · 추가·이름 바꾸기·순서·삭제) · «편집 권한»(시스템 관리자 · 주기·거두기)
//   · «전체 이력»(시스템 관리자 · 한 일 거르기 · 100건씩 쪽 넘기기 · 완전 삭제된 계약 표시)
//   · «알림»(C4b · 시스템 관리자 · ContractNotifyPanel). 들어올 때 state.tab 이 보이는 탭이면 그 탭으로 연다.
// 숨김은 편의이고 막는 것은 서버다(require_contract_level — 부서 쓰기 = 편집 · 편집 권한·전체 이력 = 시스템 관리자).
// 탭은 처음 누를 때 그 패널을 읽고, 한 번 연 패널은 내리지 않고 hidden 으로만 숨긴다(거르기·쪽 상태가 남는다).
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import { contractsAPI } from '../../api/client';
import { alertDialog, confirmDialog, promptDialog, toast } from '../../components/AppDialog';
import { COLORS, btn, card, input } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import { EVENT_LABELS, FIELD_LABELS, errorText, eventLabel, eventValueText } from './contractLabels';
import ContractNotifyPanel from './ContractNotifyPanel';

const DENIED = '설정은 편집 등급만 볼 수 있습니다. 등급이 바뀌었으면 새로고침해 주세요.';
const LOCKED = '쓰는 계약이 있어 지울 수 없습니다';
const SORT_ORDER_MAX = 9999;   // 서버 _SORT_ORDER_MAX — 순서 값은 (화면 순번) × 10 으로 매긴다
const EVENT_PAGE = 100;
const LEVEL_LABELS = { admin: '시스템 관리자', edit: '편집', view: '열람' };
// 등급 이력은 field="user"(contracts.py grant_edit·revoke_edit) — contractLabels 에 없어 이 화면에서 더한다.
// 알림 설정 이력은 field="channel"·"fields"(contracts.py put_notify_settings).
const EVENT_FIELD_LABELS = { ...FIELD_LABELS, user: '사람', channel: '알림 채널', fields: '알림 글 항목' };
const DEPT_HEADERS = ['순서', '부서 이름', '쓰는 계약', '작업'];
const MEMBER_HEADERS = ['이름', '이메일', '등급', '계약 메뉴', '준 사람', '준 날', '작업'];
const EVENT_HEADERS = ['때', '한 사람', '계약', '한 일', '칸', '전 → 후'];
const th = { padding: '9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '8px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const linkBtn = { padding: 0, border: 'none', background: 'none', color: COLORS.primaryDark, cursor: 'pointer', fontSize: '13px', fontWeight: 600, textAlign: 'left' };
const small = { color: COLORS.textMuted, fontSize: '11px' };
const note = { padding: '10px 12px', fontSize: '12px', color: COLORS.textSub };
const empty = { padding: '24px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' };
const isBlank = (v) => v === null || v === undefined || v === '';
const text = (v) => (isBlank(v) ? '—' : String(v));
const count = (n) => (Number(n) || 0).toLocaleString('ko-KR');
const offStyle = (style, off) => ({ ...style, opacity: off ? 0.5 : 1, cursor: off ? 'not-allowed' : 'pointer' });

// 패널 공용 — 요청 번호로 늦은 응답을 버리고(화면을 떠나면 전부 버린다) · 쓰기 요청 수를 센다(도는 동안 쓰기 단추를 끈다).
function useRequestGuards() {
  const mountedRef = useRef(true);
  const seqRef = useRef(0);
  const actionsRef = useRef(0);
  const [actions, setActions] = useState(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      seqRef.current += 1;
    };
  }, []);
  // 새 읽기 번호를 받고, 응답 때 «아직 가장 나중 읽기인가»를 묻는 함수를 돌려준다.
  const nextSeq = () => {
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    return () => mountedRef.current && seqRef.current === seq;
  };
  const begin = () => { actionsRef.current += 1; setActions(actionsRef.current); };
  const end = () => { actionsRef.current -= 1; if (mountedRef.current) setActions(actionsRef.current); };
  // 쓰기 한 번 — 성공이면 { response }, 실패면 { failure }.
  const write = async (request) => {
    begin();
    try {
      return { response: await request() };
    } catch (failure) {
      return { failure };
    } finally {
      end();
    }
  };
  return { mountedRef, actionsRef, busy: actions > 0, begin, end, nextSeq, write };
}

function Loading() {
  return <div role="status" style={{ color: COLORS.textMuted, fontSize: '12px', marginBottom: '8px' }}>불러오는 중…</div>;
}

// 읽기 실패(403 포함) — «0개» 안내로 그리지 않고 문구 + «다시 시도».
function LoadError({ message, onRetry }) {
  return (
    <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
      <span>{message}</span>
      <button type="button" onClick={onRetry} style={btn('secondary', 'sm')}>다시 시도</button>
    </div>
  );
}

// «부서» — 편집 등급 이상. 순번·쓰는 계약 수(휴지통 포함) · 추가·이름 바꾸기·위로/아래로·삭제(쓰는 계약이 있으면 꺼짐).
function DepartmentsPanel() {
  const { mountedRef, actionsRef, busy, begin, end, nextSeq, write } = useRequestGuards();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [newName, setNewName] = useState('');

  // 쓰기 뒤의 다시 읽기도 새 번호를 받는다 → 그 전 읽기가 늦게 와도 지운 행이 되살아나지 않는다.
  const load = () => {
    const isCurrent = nextSeq();
    setLoading(true);
    contractsAPI.departments()
      .then((r) => {
        if (!isCurrent()) return;
        setItems(Array.isArray(r.data) ? r.data : []);
        setLoadError('');
      })
      .catch(async (err) => {
        const message = await errorText(err, '부서를 불러오지 못했습니다.');
        if (!isCurrent()) return;
        setItems(null);
        setLoadError(message);
      })
      .finally(() => { if (isCurrent()) setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const addDepartment = async () => {
    const name = newName.trim();
    if (!name || actionsRef.current > 0) return;
    const { failure } = await write(() => contractsAPI.createDepartment(name));
    if (!mountedRef.current) return;
    if (!failure) {
      setNewName('');
      toast('부서를 추가했습니다.');
      load();
      return;
    }
    // 입력은 그대로 두고 목록은 다시 읽지 않는다.
    await alertDialog(await errorText(failure, '부서를 추가하지 못했습니다.'));
  };

  const renameDepartment = async (row) => {
    if (actionsRef.current > 0) return;
    const value = await promptDialog('새 부서 이름', row.name);
    if (value === null || value === undefined) return;
    const name = String(value).trim();
    if (!name || name === row.name) return;
    const used = Number(row.contract_count) || 0;
    if (used > 0 && !(await confirmDialog(`이 이름을 쓰는 계약 ${count(used)}건의 부서도 함께 바뀝니다. 바꿀까요?`))) return;
    // 대화창이 떠 있는 사이 다른 쓰기가 시작됐으면 그만둔다.
    if (!mountedRef.current || actionsRef.current > 0) return;
    const { response, failure } = await write(() => contractsAPI.updateDepartment(row.id, { name }));
    if (!mountedRef.current) return;
    if (!failure) {
      const renamed = Number(response?.data?.renamed_contracts) || 0;
      toast(renamed > 0 ? `부서 이름을 바꿨습니다(계약 ${count(renamed)}건 함께).` : '부서 이름을 바꿨습니다.');
      load();
      return;
    }
    await alertDialog(await errorText(failure, '부서 이름을 바꾸지 못했습니다.'));
    if (mountedRef.current && failure?.response?.status === 404) load();
  };

  const list = Array.isArray(items) ? items : [];
  const canReorder = list.length * 10 <= SORT_ORDER_MAX;

  // 바뀐 화면 순서 전체에 (순번) × 10 을 매기고, 지금 값과 다른 행만 위에서부터 하나씩 보낸다(앞 요청이 끝난 뒤 다음).
  const moveDepartment = async (index, delta) => {
    const target = index + delta;
    if (actionsRef.current > 0 || !canReorder || target < 0 || target >= list.length) return;
    const next = list.slice();
    [next[index], next[target]] = [next[target], next[index]];
    const changes = next
      .map((row, i) => ({ id: row.id, sortOrder: (i + 1) * 10, current: row.sort_order }))
      .filter((change) => change.sortOrder !== change.current);
    begin();
    let failure = null;
    try {
      for (const change of changes) {
        await contractsAPI.updateDepartment(change.id, { sort_order: change.sortOrder });
      }
    } catch (err) {
      failure = err;   // 거기서 멈춘다 — 순서가 반쯤 바뀌었을 수 있어 아래에서 다시 읽는다
    } finally {
      end();
    }
    if (!mountedRef.current) return;
    if (failure) await alertDialog(await errorText(failure, '순서를 바꾸지 못했습니다.'));
    // 성공·실패 모두 서버 순서가 정본이다.
    if (mountedRef.current) load();
  };

  const deleteDepartment = async (row) => {
    if (actionsRef.current > 0 || (Number(row.contract_count) || 0) > 0) return;
    if (!(await confirmDialog(`부서 «${row.name}»을 지울까요?`))) return;
    if (!mountedRef.current || actionsRef.current > 0) return;
    const { failure } = await write(() => contractsAPI.deleteDepartment(row.id));
    if (!mountedRef.current) return;
    if (!failure) {
      toast('부서를 지웠습니다.');
      load();
      return;
    }
    // 409 = 그 사이 누가 이 부서로 계약을 등록했다 · 404 = 이미 지워졌다 — 알리고 다시 읽는다.
    await alertDialog(await errorText(failure, '부서를 지우지 못했습니다.'));
    if (mountedRef.current) load();
  };

  const addOff = busy || !newName.trim();
  return (
    <>
      {loading && <Loading />}
      {loadError && <LoadError message={loadError} onRetry={load} />}
      {!loadError && Array.isArray(items) && (
        <>
          <section style={{ ...card, padding: 0, overflow: 'hidden', marginBottom: '12px' }}>
            <div style={note}>쓰는 계약 수에는 휴지통의 계약도 들어갑니다.</div>
            {!canReorder && <div style={{ ...note, color: COLORS.warn }}>부서가 너무 많아 순서를 바꿀 수 없습니다</div>}
            {list.length === 0 ? (
              <div style={empty}>부서가 없습니다. 아래에서 추가해 주세요.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table aria-label="부서" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                  <thead><tr>{DEPT_HEADERS.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {list.map((row, index) => {
                      const locked = (Number(row.contract_count) || 0) > 0;
                      const upOff = busy || !canReorder || index === 0;
                      const downOff = busy || !canReorder || index === list.length - 1;
                      return (
                        <tr key={row.id} data-testid="contract-department-row">
                          <td style={td}>{index + 1}</td>
                          <td style={td}>{text(row.name)}</td>
                          <td style={td}>{count(row.contract_count)}</td>
                          <td style={td}>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <button type="button" onClick={() => renameDepartment(row)} disabled={busy}
                                style={offStyle(btn('secondary', 'sm'), busy)}>이름 바꾸기</button>
                              <button type="button" onClick={() => moveDepartment(index, -1)} disabled={upOff}
                                style={offStyle(btn('secondary', 'sm'), upOff)}>위로</button>
                              <button type="button" onClick={() => moveDepartment(index, 1)} disabled={downOff}
                                style={offStyle(btn('secondary', 'sm'), downOff)}>아래로</button>
                              <button type="button" onClick={() => deleteDepartment(row)} disabled={busy || locked}
                                title={locked ? LOCKED : undefined}
                                style={offStyle(btn('danger', 'sm'), busy || locked)}>삭제</button>
                              {locked && <span style={small}>{LOCKED}</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <form
            noValidate
            onSubmit={(event) => { event.preventDefault(); addDepartment(); }}
            style={{ ...card, padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}
          >
            <input type="text" aria-label="새 부서 이름" placeholder="새 부서 이름" value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{ ...input, width: '260px' }} />
            <button type="submit" disabled={addOff} style={offStyle(btn('primary', 'sm'), addOff)}>추가</button>
          </form>
        </>
      )}
    </>
  );
}

// «편집 권한» — 시스템 관리자. 계약 메뉴가 열린 사람 + 편집 행이 남은 사람(서버 GET /members).
function MembersPanel() {
  const { mountedRef, actionsRef, busy, nextSeq, write } = useRequestGuards();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = () => {
    const isCurrent = nextSeq();
    setLoading(true);
    contractsAPI.members()
      .then((r) => {
        if (!isCurrent()) return;
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        setLoadError('');
      })
      .catch(async (err) => {
        const message = await errorText(err, '편집 권한을 불러오지 못했습니다.');
        if (!isCurrent()) return;
        setItems(null);
        setLoadError(message);
      })
      .finally(() => { if (isCurrent()) setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const changeLevel = async (member, edit) => {
    if (actionsRef.current > 0) return;
    if (!edit) {
      if (!(await confirmDialog(`«${member.name || member.email}»의 편집 권한을 거둘까요?`))) return;
      if (!mountedRef.current || actionsRef.current > 0) return;
    }
    const { response, failure } = await write(() => contractsAPI.setMember(member.user_id, edit));
    if (!mountedRef.current) return;
    if (!failure) {
      const updated = response?.data;
      // 응답 항목으로 그 행만 바꾼다(정렬은 다시 읽을 때까지 그대로).
      if (updated && updated.user_id === member.user_id) {
        setItems((prev) => (Array.isArray(prev) ? prev.map((m) => (m.user_id === member.user_id ? updated : m)) : prev));
      } else {
        load();
      }
      toast(edit ? '편집 권한을 줬습니다.' : '편집 권한을 거뒀습니다.');
      return;
    }
    await alertDialog(await errorText(failure, '등급을 바꾸지 못했습니다.'));
    if (mountedRef.current) load();
  };

  const list = Array.isArray(items) ? items : [];
  return (
    <>
      {loading && <Loading />}
      {loadError && <LoadError message={loadError} onRetry={load} />}
      {!loadError && Array.isArray(items) && (
        <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={note}>계약 메뉴 권한이 있는 사람만 여기 나옵니다. 메뉴 권한은 관리자 → 사용자 권한에서 줍니다.</div>
          {list.length === 0 ? (
            <div style={empty}>계약 메뉴 권한이 있는 사람이 없습니다.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table aria-label="편집 권한" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
                <thead><tr>{MEMBER_HEADERS.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {list.map((m) => {
                    const giveOff = busy || !m.menu_open;
                    return (
                      <tr key={m.user_id} data-testid="contract-member-row">
                        <td style={td}>
                          {text(m.name)}
                          {m.is_active === false && <span style={{ color: COLORS.textMuted }}> (비활성)</span>}
                        </td>
                        <td style={td}>{text(m.email)}</td>
                        <td style={td}>{LEVEL_LABELS[m.level] || text(m.level)}</td>
                        <td style={td}>{m.menu_open ? '있음' : '없음'}</td>
                        <td style={td}>{text(m.granted_by_name)}</td>
                        <td style={td}>{fmtKstDateTime(m.granted_at) || '—'}</td>
                        <td style={td}>
                          {/* 거두기는 메뉴가 닫혔거나 비활성이어도 켠다 — 서버가 같은 상태 검사를 메뉴 검사보다 먼저 한다. */}
                          {m.level === 'edit' && (
                            <button type="button" onClick={() => changeLevel(m, false)} disabled={busy}
                              style={offStyle(btn('danger', 'sm'), busy)}>편집 거두기</button>
                          )}
                          {m.level === 'view' && (
                            <button type="button" onClick={() => changeLevel(m, true)} disabled={giveOff}
                              style={offStyle(btn('secondary', 'sm'), giveOff)}>편집 주기</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </>
  );
}

// «전체 이력» — 시스템 관리자. 최신순 100건씩 · 한 일로 거르기 · 완전 삭제된 계약은 번호 글자만 남는다.
function EventsPanel({ onOpenContract }) {
  const { nextSeq } = useRequestGuards();
  const [action, setAction] = useState('');      // «한 일» 거르기(빈 값 = 전체)
  const [page, setPage] = useState(null);        // 마지막으로 읽은 쪽 { items, total, offset }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [failed, setFailed] = useState(null);    // 실패한 읽기 { offset, action } — «다시 시도»가 그대로 다시 읽는다

  const load = (offset, nextAction) => {
    const isCurrent = nextSeq();
    const params = { limit: EVENT_PAGE, offset };
    if (nextAction) params.action = nextAction;   // «전체»면 action 을 싣지 않는다
    setLoading(true);
    contractsAPI.events(params)
      .then((r) => {
        if (!isCurrent()) return;
        setPage({ items: Array.isArray(r.data?.items) ? r.data.items : [], total: Number(r.data?.total) || 0, offset });
        setLoadError('');
        setFailed(null);
      })
      .catch(async (err) => {
        const message = await errorText(err, '이력을 불러오지 못했습니다.');
        if (!isCurrent()) return;
        setPage(null);
        setLoadError(message);
        setFailed({ offset, action: nextAction });
      })
      .finally(() => { if (isCurrent()) setLoading(false); });
  };

  useEffect(() => { load(0, ''); }, []);

  const changeAction = (value) => {
    setAction(value);
    load(0, value);
  };
  const retry = () => { if (failed) load(failed.offset, failed.action); };

  const items = page?.items || [];
  const total = page?.total || 0;
  const offset = page?.offset || 0;
  // 읽는 동안 쪽 단추를 끈다 — 연타로 offset 이 두 칸 뛰지 않게.
  const prevOff = loading || offset === 0;
  const nextOff = loading || offset + items.length >= total;

  const contractCell = (ev) => {
    if (!isBlank(ev.contract_id)) {
      return (
        <>
          <button type="button" onClick={() => onOpenContract(ev.contract_id)} style={linkBtn}>{ev.contract_no || `#${ev.contract_id}`}</button>
          {ev.contract_name && <span style={small}> {ev.contract_name}</span>}
        </>
      );
    }
    if (!isBlank(ev.contract_no)) return <>{ev.contract_no}<span style={small}> (완전 삭제됨)</span></>;
    return '—';
  };

  return (
    <>
      <section aria-label="이력 거르기" style={{ ...card, padding: '12px 14px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
        <label style={{ fontSize: '12px' }}>한 일 <select aria-label="한 일" value={action} onChange={(e) => changeAction(e.target.value)} style={{ ...input, width: '180px', marginLeft: '4px' }}>
          <option value="">전체</option>
          {Object.entries(EVENT_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
      </section>
      {loading && <Loading />}
      {loadError && <LoadError message={loadError} onRetry={retry} />}
      {!loadError && page && (
        <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {items.length === 0 ? (
            <div style={empty}>이력이 없습니다.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table aria-label="전체 이력" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead><tr>{EVENT_HEADERS.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {items.map((ev, index) => {
                    const hasChange = !isBlank(ev.old_value) || !isBlank(ev.new_value);
                    return (
                      <tr key={ev.id ?? index} data-testid="contract-global-event">
                        <td style={td}>{fmtKstDateTime(ev.created_at) || '—'}</td>
                        <td style={td}>{ev.actor_name || '—'}</td>
                        <td style={td}>{contractCell(ev)}</td>
                        <td style={td}>{eventLabel(ev.action)}</td>
                        <td style={td}>{EVENT_FIELD_LABELS[ev.field] || ev.field || '—'}</td>
                        <td style={{ ...td, whiteSpace: 'normal' }}>{hasChange ? `${eventValueText(ev.field, ev.old_value)} → ${eventValueText(ev.field, ev.new_value)}` : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {(items.length > 0 || offset > 0) && (
            <div style={{ ...note, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{items.length > 0 ? `${count(total)}건 중 ${count(offset + 1)}–${count(offset + items.length)}` : `${count(total)}건`}</span>
              <button type="button" onClick={() => load(Math.max(0, offset - EVENT_PAGE), action)} disabled={prevOff}
                style={offStyle(btn('secondary', 'sm'), prevOff)}>이전</button>
              <button type="button" onClick={() => load(offset + EVENT_PAGE, action)} disabled={nextOff}
                style={offStyle(btn('secondary', 'sm'), nextOff)}>다음</button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

// 보이는 탭 — 렌더와 «처음 열 탭 정하기»(등급 읽기 뒤)가 같은 식을 쓴다.
const visibleTabs = (can) => (can?.edit ? [
  { key: 'departments', label: '부서' },
  can.grant && { key: 'members', label: '편집 권한' },
  can.settings && { key: 'events', label: '전체 이력' },
  can.settings && { key: 'notify', label: '알림' },
].filter(Boolean) : []);

export default function ContractSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // 등급 — null = 판정 전(탭·거부 문구를 그리지 않는다) · 읽기 실패는 모두 false(단추를 숨기는 쪽으로 틀린다).
  const [can, setCan] = useState(null);
  const [tab, setTab] = useState('departments');
  const [opened, setOpened] = useState({ departments: true });   // 한 번 연 패널(내리지 않는다)
  const mountedRef = useRef(true);
  // 들어올 때 받은 탭(목록 위 알림 실패 안내 → { state: { tab: 'notify' } }) — 보이는 탭일 때만 연다.
  const wantedTabRef = useRef(location.state?.tab);

  useEffect(() => {
    mountedRef.current = true;
    contractsAPI.me()
      .then((r) => {
        if (!mountedRef.current) return;
        const c = r.data?.can || {};
        const next = { edit: c.edit === true, settings: c.settings === true, grant: c.grant === true };
        setCan(next);
        // 보이는 탭은 등급을 읽어야 안다 — 같은 등급 값으로 판정하고, 패널도 열린 것으로 표시해야 그려진다.
        const wanted = wantedTabRef.current;
        if (typeof wanted === 'string' && visibleTabs(next).some((t) => t.key === wanted)) {
          setTab(wanted);
          setOpened((prev) => ({ ...prev, [wanted]: true }));
        }
      })
      .catch(() => {
        if (mountedRef.current) setCan({ edit: false, settings: false, grant: false });
      });
    return () => { mountedRef.current = false; };
  }, []);

  const tabs = visibleTabs(can);
  const openTab = (key) => {
    setTab(key);
    setOpened((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };
  const panel = (key, content) => (opened[key] && tabs.some((t) => t.key === key) ? (
    <div role="tabpanel" id={`contract-settings-panel-${key}`} aria-labelledby={`contract-settings-tab-${key}`} hidden={tab !== key}>
      {content}
    </div>
  ) : null);

  return (
    <AppShell app="contracts">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        <div style={{ padding: '20px 24px', maxWidth: '1450px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <h2 style={{ margin: 0, fontSize: '20px' }}>설정</h2>
            <button type="button" onClick={() => navigate('/contracts')} style={btn('secondary', 'sm')}>← 계약 목록</button>
            {can === null && <span role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</span>}
          </div>
          {can !== null && !can.edit && (
            <div style={{ ...card, color: COLORS.textSub, fontSize: '13px' }}>{DENIED}</div>
          )}
          {can?.edit && (
            <>
              <div role="tablist" aria-label="계약 설정" style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${COLORS.border}`, marginBottom: '12px' }}>
                {tabs.map(({ key, label }) => (
                  <button key={key} type="button" role="tab" id={`contract-settings-tab-${key}`} aria-selected={tab === key}
                    aria-controls={`contract-settings-panel-${key}`} onClick={() => openTab(key)}
                    style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${tab === key ? COLORS.primary : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: tab === key ? 700 : 500, color: tab === key ? COLORS.primaryDark : COLORS.textSub }}>
                    {label}
                  </button>
                ))}
              </div>
              {panel('departments', <DepartmentsPanel />)}
              {panel('members', <MembersPanel />)}
              {panel('events', <EventsPanel onOpenContract={(id) => navigate(`/contracts/${id}`)} />)}
              {panel('notify', <ContractNotifyPanel />)}
            </>
          )}
        </div>
      </main>
    </AppShell>
  );
}
